import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Layers, Plus, Save, Eye, Code, Settings, Trash2, Edit2, Copy, Check,
  Loader2, ArrowLeft, Globe, Layout, Type, Image, Square, List, Mail,
  Phone, User, Calendar, Hash, FileText, Link2, Grip, ChevronUp, ChevronDown,
  ExternalLink, Sparkles, Target, BarChart3, MousePointer, Zap, Video, GripVertical
} from 'lucide-react';
import api, { landingPageAPI, campaignsAPI } from '../api';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SECTION_TYPES = [
  { value: 'hero', label: 'Hero Section', icon: Layout, description: 'Large banner with headline and CTA' },
  { value: 'text', label: 'Text Block', icon: Type, description: 'Rich text content' },
  { value: 'image', label: 'Image', icon: Image, description: 'Single image with optional caption' },
  { value: 'features', label: 'Features Grid', icon: Square, description: '2-4 column feature cards' },
  { value: 'testimonials', label: 'Testimonials', icon: List, description: 'Customer testimonials slider' },
  { value: 'form', label: 'Lead Form', icon: Mail, description: 'Contact/lead capture form' },
  { value: 'cta', label: 'CTA Section', icon: MousePointer, description: 'Call-to-action banner' },
  { value: 'video', label: 'Video Embed', icon: FileText, description: 'YouTube/Vimeo embed' },
];

const FORM_FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'name', label: 'Full Name', icon: User },
  { value: 'company', label: 'Company', icon: Square },
  { value: 'textarea', label: 'Text Area', icon: FileText },
  { value: 'select', label: 'Dropdown', icon: List },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'number', label: 'Number', icon: Hash },
];

// Sortable Section Item Component for drag-and-drop
const SortableSectionItem = ({ section, index, isSelected, onClick, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const typeInfo = SECTION_TYPES.find(t => t.value === section.type);
  const Icon = typeInfo?.icon || Layout;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
        isSelected ? 'border-primary bg-primary/5' : 'hover:bg-slate-50'
      } ${isDragging ? 'shadow-lg ring-2 ring-primary' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
          <Icon className="h-4 w-4" />
          <span className="text-sm font-medium">{typeInfo?.label || section.type}</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-red-500"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};


const generateId = () => Math.random().toString(36).substring(2, 9);

export const LandingPageBuilderPage = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState('design');
  const [showEmbedDialog, setShowEmbedDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showAddSectionDialog, setShowAddSectionDialog] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [copiedEmbed, setCopiedEmbed] = useState(false);

  // Page state
  const [pageData, setPageData] = useState({
    name: '',
    slug: '',
    description: '',
    status: 'draft',
    campaign_id: '',
    meta_title: '',
    meta_description: '',
    og_image: '',
    sections: [],
    form_settings: {
      submit_button_text: 'Submit',
      success_message: 'Thank you! We\'ll be in touch soon.',
      redirect_url: '',
      create_contact: true,
      create_lead: true,
      notify_email: '',
    },
    styling: {
      primary_color: '#3B82F6',
      background_color: '#FFFFFF',
      text_color: '#1F2937',
      font_family: 'Inter',
    },
  });

  useEffect(() => {
    if (pageId && pageId !== 'new') {
      fetchPage();
    } else {
      setLoading(false);
    }
    fetchCampaigns();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      const res = await landingPageAPI.getById(pageId);
      const data = res.data;
      setPage(data);
      setPageData({
        name: data.name || '',
        slug: data.slug || '',
        description: data.description || '',
        status: data.status || 'draft',
        campaign_id: data.campaign_id || '',
        meta_title: data.meta_title || '',
        meta_description: data.meta_description || '',
        og_image: data.og_image || '',
        sections: data.sections || [],
        form_settings: data.form_settings || {
          submit_button_text: 'Submit',
          success_message: 'Thank you! We\'ll be in touch soon.',
          redirect_url: '',
          create_contact: true,
          create_lead: true,
          notify_email: '',
        },
        styling: data.styling || {
          primary_color: '#3B82F6',
          background_color: '#FFFFFF',
          text_color: '#1F2937',
          font_family: 'Inter',
        },
      });
    } catch (error) {
      console.error('Failed to fetch page:', error);
      toast.error('Failed to load landing page');
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const res = await campaignsAPI.getAll({ limit: 100 });
      setCampaigns(res.data?.campaigns || res.data || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const handleSave = async () => {
    if (!pageData.name) {
      toast.error('Page name is required');
      return;
    }

    try {
      setSaving(true);
      
      // Generate slug if empty
      if (!pageData.slug) {
        pageData.slug = pageData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      }

      if (pageId && pageId !== 'new') {
        await landingPageAPI.update(pageId, pageData);
        toast.success('Landing page saved');
      } else {
        const res = await landingPageAPI.create(pageData);
        toast.success('Landing page created');
        navigate(`/marketing/landing-pages/${res.data.id}`, { replace: true });
      }
    } catch (error) {
      toast.error('Failed to save landing page');
    } finally {
      setSaving(false);
    }
  };

  const addSection = (type) => {
    const sectionDefaults = {
      hero: {
        type: 'hero',
        headline: 'Your Compelling Headline',
        subheadline: 'A supporting statement that explains your value proposition.',
        cta_text: 'Get Started',
        cta_link: '#form',
        background_image: '',
        background_color: pageData.styling.primary_color,
      },
      text: {
        type: 'text',
        content: '<p>Your content here...</p>',
        alignment: 'left',
      },
      image: {
        type: 'image',
        url: '',
        alt: '',
        caption: '',
        width: '100%',
      },
      features: {
        type: 'features',
        headline: 'Our Features',
        items: [
          { icon: 'check', title: 'Feature 1', description: 'Description of feature 1' },
          { icon: 'check', title: 'Feature 2', description: 'Description of feature 2' },
          { icon: 'check', title: 'Feature 3', description: 'Description of feature 3' },
        ],
        columns: 3,
      },
      testimonials: {
        type: 'testimonials',
        headline: 'What Our Customers Say',
        items: [
          { quote: 'This product changed my life!', author: 'John Doe', company: 'Acme Inc' },
        ],
      },
      form: {
        type: 'form',
        headline: 'Get In Touch',
        subheadline: 'Fill out the form below and we\'ll get back to you.',
        fields: [
          { id: generateId(), type: 'name', label: 'Full Name', required: true, placeholder: 'Enter your name' },
          { id: generateId(), type: 'email', label: 'Email', required: true, placeholder: 'you@example.com' },
          { id: generateId(), type: 'phone', label: 'Phone', required: false, placeholder: '+1 (555) 000-0000' },
          { id: generateId(), type: 'textarea', label: 'Message', required: false, placeholder: 'How can we help?' },
        ],
      },
      cta: {
        type: 'cta',
        headline: 'Ready to Get Started?',
        subheadline: 'Join thousands of satisfied customers today.',
        cta_text: 'Sign Up Now',
        cta_link: '#form',
        background_color: pageData.styling.primary_color,
      },
      video: {
        type: 'video',
        url: '',
        autoplay: false,
      },
    };

    const newSection = {
      id: generateId(),
      ...sectionDefaults[type],
    };

    setPageData(prev => ({
      ...prev,
      sections: [...prev.sections, newSection],
    }));
    
    setShowAddSectionDialog(false);
    setEditingSectionIndex(pageData.sections.length);
    toast.success(`${SECTION_TYPES.find(s => s.value === type)?.label} added`);
  };

  const updateSection = (index, updates) => {
    setPageData(prev => ({
      ...prev,
      sections: prev.sections.map((s, i) => i === index ? { ...s, ...updates } : s),
    }));
  };

  const removeSection = (index) => {
    if (!window.confirm('Remove this section?')) return;
    setPageData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index),
    }));
    setEditingSectionIndex(null);
    toast.success('Section removed');
  };

  const moveSection = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= pageData.sections.length) return;
    
    const newSections = [...pageData.sections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    
    setPageData(prev => ({ ...prev, sections: newSections }));
    setEditingSectionIndex(newIndex);
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = pageData.sections.findIndex((s) => s.id === active.id);
      const newIndex = pageData.sections.findIndex((s) => s.id === over.id);
      
      setPageData(prev => ({
        ...prev,
        sections: arrayMove(prev.sections, oldIndex, newIndex),
      }));
      
      // Update editing index if needed
      if (editingSectionIndex === oldIndex) {
        setEditingSectionIndex(newIndex);
      } else if (editingSectionIndex === newIndex) {
        setEditingSectionIndex(oldIndex);
      }
    }
  };

  const addFormField = (sectionIndex) => {
    const newField = {
      id: generateId(),
      type: 'text',
      label: 'New Field',
      required: false,
      placeholder: '',
    };
    
    setPageData(prev => ({
      ...prev,
      sections: prev.sections.map((s, i) => {
        if (i === sectionIndex && s.type === 'form') {
          return { ...s, fields: [...(s.fields || []), newField] };
        }
        return s;
      }),
    }));
  };

  const updateFormField = (sectionIndex, fieldIndex, updates) => {
    setPageData(prev => ({
      ...prev,
      sections: prev.sections.map((s, i) => {
        if (i === sectionIndex && s.type === 'form') {
          return {
            ...s,
            fields: s.fields.map((f, fi) => fi === fieldIndex ? { ...f, ...updates } : f),
          };
        }
        return s;
      }),
    }));
  };

  const removeFormField = (sectionIndex, fieldIndex) => {
    setPageData(prev => ({
      ...prev,
      sections: prev.sections.map((s, i) => {
        if (i === sectionIndex && s.type === 'form') {
          return { ...s, fields: s.fields.filter((_, fi) => fi !== fieldIndex) };
        }
        return s;
      }),
    }));
  };

  const getEmbedCode = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `<!-- UnifiCRM Landing Page Embed -->
<div id="unifi-landing-${pageData.slug}"></div>
<script>
(function() {
  var container = document.getElementById('unifi-landing-${pageData.slug}');
  var iframe = document.createElement('iframe');
  iframe.src = '${baseUrl}/embed/landing/${pageData.slug || pageId}';
  iframe.style.width = '100%';
  iframe.style.border = 'none';
  iframe.style.minHeight = '600px';
  iframe.onload = function() {
    // Auto-resize iframe based on content
    window.addEventListener('message', function(e) {
      if (e.data.type === 'unifi-resize') {
        iframe.style.height = e.data.height + 'px';
      }
    });
  };
  container.appendChild(iframe);
})();
</script>`;
  };

  const getFormOnlyEmbedCode = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const formSection = pageData.sections.find(s => s.type === 'form');
    if (!formSection) return '<!-- No form found on this page -->';
    
    return `<!-- UnifiCRM Form Embed -->
<div id="unifi-form-${pageData.slug}"></div>
<script>
(function() {
  var container = document.getElementById('unifi-form-${pageData.slug}');
  var iframe = document.createElement('iframe');
  iframe.src = '${baseUrl}/embed/form/${pageData.slug || pageId}';
  iframe.style.width = '100%';
  iframe.style.border = 'none';
  iframe.style.minHeight = '400px';
  container.appendChild(iframe);
})();
</script>`;
  };

  const copyEmbedCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedEmbed(true);
    setTimeout(() => setCopiedEmbed(false), 2000);
    toast.success('Embed code copied!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="landing-page-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/marketing/landing-pages')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Layers className="h-6 w-6" />
              {pageId === 'new' ? 'Create Landing Page' : 'Edit Landing Page'}
            </h1>
            <p className="text-muted-foreground">
              Build and publish landing pages with lead capture forms
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowPreviewDialog(true)}>
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>
          {pageId !== 'new' && (
            <Button variant="outline" onClick={() => setShowEmbedDialog(true)}>
              <Code className="h-4 w-4 mr-2" />
              Embed
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left Panel - Section List */}
        <div className="col-span-1 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center justify-between">
                Page Sections
                <Button size="sm" onClick={() => setShowAddSectionDialog(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {pageData.sections.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Layout className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No sections yet</p>
                  <Button 
                    variant="link" 
                    size="sm"
                    onClick={() => setShowAddSectionDialog(true)}
                  >
                    Add your first section
                  </Button>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={pageData.sections.map(s => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {pageData.sections.map((section, idx) => (
                        <SortableSectionItem
                          key={section.id}
                          section={section}
                          index={idx}
                          isSelected={editingSectionIndex === idx}
                          onClick={() => setEditingSectionIndex(idx)}
                          onRemove={() => removeSection(idx)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          {page && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Performance
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold">{page.views || 0}</p>
                    <p className="text-xs text-muted-foreground">Views</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{page.submissions || 0}</p>
                    <p className="text-xs text-muted-foreground">Submissions</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-2xl font-bold">
                      {page.views > 0 ? ((page.submissions / page.views) * 100).toFixed(1) : 0}%
                    </p>
                    <p className="text-xs text-muted-foreground">Conversion Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Panel - Editor */}
        <div className="col-span-2">
          <Card>
            <CardContent className="p-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                  <TabsTrigger value="design">Design</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                  <TabsTrigger value="seo">SEO</TabsTrigger>
                  <TabsTrigger value="form">Form Settings</TabsTrigger>
                </TabsList>

                <TabsContent value="design" className="space-y-6 pt-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Page Name *</Label>
                      <Input
                        value={pageData.name}
                        onChange={(e) => setPageData({ ...pageData, name: e.target.value })}
                        placeholder="e.g., Product Launch 2024"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>URL Slug</Label>
                      <div className="flex">
                        <span className="inline-flex items-center px-3 text-sm text-muted-foreground bg-slate-100 border border-r-0 rounded-l-md">
                          /p/
                        </span>
                        <Input
                          value={pageData.slug}
                          onChange={(e) => setPageData({ ...pageData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                          placeholder="product-launch-2024"
                          className="rounded-l-none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section Editor */}
                  {editingSectionIndex !== null && pageData.sections[editingSectionIndex] && (
                    <Card className="border-primary">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center justify-between">
                          Edit: {SECTION_TYPES.find(t => t.value === pageData.sections[editingSectionIndex].type)?.label}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingSectionIndex(null)}
                          >
                            Done
                          </Button>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        {renderSectionEditor(pageData.sections[editingSectionIndex], editingSectionIndex)}
                      </CardContent>
                    </Card>
                  )}

                  {/* Styling */}
                  <div className="space-y-4 border-t pt-4">
                    <h4 className="font-medium">Page Styling</h4>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label>Primary Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={pageData.styling.primary_color}
                            onChange={(e) => setPageData({ 
                              ...pageData, 
                              styling: { ...pageData.styling, primary_color: e.target.value } 
                            })}
                            className="w-12 h-10 p-1"
                          />
                          <Input
                            value={pageData.styling.primary_color}
                            onChange={(e) => setPageData({ 
                              ...pageData, 
                              styling: { ...pageData.styling, primary_color: e.target.value } 
                            })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Background</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={pageData.styling.background_color}
                            onChange={(e) => setPageData({ 
                              ...pageData, 
                              styling: { ...pageData.styling, background_color: e.target.value } 
                            })}
                            className="w-12 h-10 p-1"
                          />
                          <Input
                            value={pageData.styling.background_color}
                            onChange={(e) => setPageData({ 
                              ...pageData, 
                              styling: { ...pageData.styling, background_color: e.target.value } 
                            })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Text Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={pageData.styling.text_color}
                            onChange={(e) => setPageData({ 
                              ...pageData, 
                              styling: { ...pageData.styling, text_color: e.target.value } 
                            })}
                            className="w-12 h-10 p-1"
                          />
                          <Input
                            value={pageData.styling.text_color}
                            onChange={(e) => setPageData({ 
                              ...pageData, 
                              styling: { ...pageData.styling, text_color: e.target.value } 
                            })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Font</Label>
                        <Select 
                          value={pageData.styling.font_family}
                          onValueChange={(v) => setPageData({ 
                            ...pageData, 
                            styling: { ...pageData.styling, font_family: v } 
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Inter">Inter</SelectItem>
                            <SelectItem value="Roboto">Roboto</SelectItem>
                            <SelectItem value="Open Sans">Open Sans</SelectItem>
                            <SelectItem value="Lato">Lato</SelectItem>
                            <SelectItem value="Poppins">Poppins</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="settings" className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select 
                        value={pageData.status}
                        onValueChange={(v) => setPageData({ ...pageData, status: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="published">Published</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Link to Campaign</Label>
                      <Select 
                        value={pageData.campaign_id || 'none'}
                        onValueChange={(v) => setPageData({ ...pageData, campaign_id: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select campaign" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {campaigns.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={pageData.description}
                      onChange={(e) => setPageData({ ...pageData, description: e.target.value })}
                      placeholder="Internal description for this page..."
                      rows={3}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="seo" className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Meta Title</Label>
                    <Input
                      value={pageData.meta_title}
                      onChange={(e) => setPageData({ ...pageData, meta_title: e.target.value })}
                      placeholder="Page title for search engines"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Meta Description</Label>
                    <Textarea
                      value={pageData.meta_description}
                      onChange={(e) => setPageData({ ...pageData, meta_description: e.target.value })}
                      placeholder="Description for search results (150-160 characters)"
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>OG Image URL</Label>
                    <Input
                      value={pageData.og_image}
                      onChange={(e) => setPageData({ ...pageData, og_image: e.target.value })}
                      placeholder="https://... (image for social sharing)"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="form" className="space-y-4 pt-4">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Zap className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-blue-900">Form Submission Handling</h4>
                          <p className="text-sm text-blue-700">
                            Configure what happens when visitors submit the form
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Submit Button Text</Label>
                      <Input
                        value={pageData.form_settings.submit_button_text}
                        onChange={(e) => setPageData({ 
                          ...pageData, 
                          form_settings: { ...pageData.form_settings, submit_button_text: e.target.value } 
                        })}
                        placeholder="Submit"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Redirect URL (optional)</Label>
                      <Input
                        value={pageData.form_settings.redirect_url}
                        onChange={(e) => setPageData({ 
                          ...pageData, 
                          form_settings: { ...pageData.form_settings, redirect_url: e.target.value } 
                        })}
                        placeholder="https://... (leave empty for message)"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Success Message</Label>
                    <Textarea
                      value={pageData.form_settings.success_message}
                      onChange={(e) => setPageData({ 
                        ...pageData, 
                        form_settings: { ...pageData.form_settings, success_message: e.target.value } 
                      })}
                      placeholder="Thank you message after submission"
                      rows={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Notification Email</Label>
                    <Input
                      value={pageData.form_settings.notify_email}
                      onChange={(e) => setPageData({ 
                        ...pageData, 
                        form_settings: { ...pageData.form_settings, notify_email: e.target.value } 
                      })}
                      placeholder="you@company.com (receive notifications)"
                    />
                  </div>

                  <div className="space-y-4 pt-4 border-t">
                    <h4 className="font-medium">CRM Integration</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Create Contact in CRM</Label>
                          <p className="text-sm text-muted-foreground">Automatically create a contact record</p>
                        </div>
                        <Switch
                          checked={pageData.form_settings.create_contact}
                          onCheckedChange={(v) => setPageData({ 
                            ...pageData, 
                            form_settings: { ...pageData.form_settings, create_contact: v } 
                          })}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Create Lead</Label>
                          <p className="text-sm text-muted-foreground">Also create a lead for sales follow-up</p>
                        </div>
                        <Switch
                          checked={pageData.form_settings.create_lead}
                          onCheckedChange={(v) => setPageData({ 
                            ...pageData, 
                            form_settings: { ...pageData.form_settings, create_lead: v } 
                          })}
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Section Dialog */}
      <Dialog open={showAddSectionDialog} onOpenChange={setShowAddSectionDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Section</DialogTitle>
            <DialogDescription>Choose a section type to add to your landing page</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            {SECTION_TYPES.map(type => (
              <Card 
                key={type.value}
                className="cursor-pointer hover:border-primary transition-colors"
                onClick={() => addSection(type.value)}
              >
                <CardContent className="p-4 flex items-start gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <type.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium">{type.label}</p>
                    <p className="text-sm text-muted-foreground">{type.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Embed Code Dialog */}
      <Dialog open={showEmbedDialog} onOpenChange={setShowEmbedDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" />
              Embed Code
            </DialogTitle>
            <DialogDescription>
              Copy the code below to embed this page or form on any website
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="full">
            <TabsList>
              <TabsTrigger value="full">Full Page</TabsTrigger>
              <TabsTrigger value="form">Form Only</TabsTrigger>
            </TabsList>
            
            <TabsContent value="full" className="space-y-4">
              <div className="relative">
                <pre className="p-4 bg-slate-900 text-slate-100 rounded-lg text-sm overflow-x-auto">
                  {getEmbedCode()}
                </pre>
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => copyEmbedCode(getEmbedCode())}
                >
                  {copiedEmbed ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </TabsContent>
            
            <TabsContent value="form" className="space-y-4">
              <div className="relative">
                <pre className="p-4 bg-slate-900 text-slate-100 rounded-lg text-sm overflow-x-auto">
                  {getFormOnlyEmbedCode()}
                </pre>
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => copyEmbedCode(getFormOnlyEmbedCode())}
                >
                  {copiedEmbed ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
            <Globe className="h-4 w-4 text-blue-600" />
            <p className="text-sm text-blue-700">
              The embedded page will automatically capture form submissions and create contacts in your CRM.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Page Preview</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[70vh]">
            <div 
              className="p-8 min-h-full"
              style={{ 
                backgroundColor: pageData.styling.background_color,
                color: pageData.styling.text_color,
                fontFamily: pageData.styling.font_family,
              }}
            >
              {pageData.sections.map((section, idx) => (
                <div key={section.id} className="mb-8">
                  {renderSectionPreview(section)}
                </div>
              ))}
              {pageData.sections.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Layout className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Add sections to see a preview</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );

  function renderSectionEditor(section, index) {
    switch (section.type) {
      case 'hero':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Headline</Label>
              <Input
                value={section.headline}
                onChange={(e) => updateSection(index, { headline: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Subheadline</Label>
              <Textarea
                value={section.subheadline}
                onChange={(e) => updateSection(index, { subheadline: e.target.value })}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>CTA Button Text</Label>
                <Input
                  value={section.cta_text}
                  onChange={(e) => updateSection(index, { cta_text: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>CTA Link</Label>
                <Input
                  value={section.cta_link}
                  onChange={(e) => updateSection(index, { cta_link: e.target.value })}
                  placeholder="#form or https://..."
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Background Image URL</Label>
              <Input
                value={section.background_image}
                onChange={(e) => updateSection(index, { background_image: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={section.content}
                onChange={(e) => updateSection(index, { content: e.target.value })}
                rows={6}
                placeholder="Enter your text content (HTML supported)..."
              />
            </div>
            <div className="space-y-2">
              <Label>Alignment</Label>
              <Select 
                value={section.alignment}
                onValueChange={(v) => updateSection(index, { alignment: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'form':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Form Headline</Label>
                <Input
                  value={section.headline}
                  onChange={(e) => updateSection(index, { headline: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Subheadline</Label>
                <Input
                  value={section.subheadline}
                  onChange={(e) => updateSection(index, { subheadline: e.target.value })}
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label>Form Fields</Label>
                <Button size="sm" variant="outline" onClick={() => addFormField(index)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Field
                </Button>
              </div>
              
              <div className="space-y-3">
                {(section.fields || []).map((field, fieldIdx) => (
                  <div key={field.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="grid grid-cols-4 gap-2">
                      <Select
                        value={field.type}
                        onValueChange={(v) => updateFormField(index, fieldIdx, { type: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FORM_FIELD_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={field.label}
                        onChange={(e) => updateFormField(index, fieldIdx, { label: e.target.value })}
                        placeholder="Label"
                      />
                      <Input
                        value={field.placeholder || ''}
                        onChange={(e) => updateFormField(index, fieldIdx, { placeholder: e.target.value })}
                        placeholder="Placeholder"
                      />
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1 text-sm">
                          <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => updateFormField(index, fieldIdx, { required: e.target.checked })}
                          />
                          Required
                        </label>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500"
                          onClick={() => removeFormField(index, fieldIdx)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'cta':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Headline</Label>
              <Input
                value={section.headline}
                onChange={(e) => updateSection(index, { headline: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Subheadline</Label>
              <Input
                value={section.subheadline}
                onChange={(e) => updateSection(index, { subheadline: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Button Text</Label>
                <Input
                  value={section.cta_text}
                  onChange={(e) => updateSection(index, { cta_text: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Button Link</Label>
                <Input
                  value={section.cta_link}
                  onChange={(e) => updateSection(index, { cta_link: e.target.value })}
                />
              </div>
            </div>
          </div>
        );

      default:
        return <p className="text-muted-foreground">Editor for {section.type} coming soon...</p>;
    }
  }

  function renderSectionPreview(section) {
    const primaryColor = pageData.styling.primary_color;
    
    switch (section.type) {
      case 'hero':
        return (
          <div 
            className="py-20 px-8 text-center rounded-lg"
            style={{ 
              backgroundColor: section.background_color || primaryColor,
              backgroundImage: section.background_image ? `url(${section.background_image})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <h1 className="text-4xl font-bold text-white mb-4">{section.headline}</h1>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">{section.subheadline}</p>
            <button 
              className="px-8 py-3 bg-white text-slate-900 font-semibold rounded-lg hover:bg-slate-100 transition-colors"
            >
              {section.cta_text}
            </button>
          </div>
        );

      case 'text':
        return (
          <div className={`py-8 px-4 text-${section.alignment || 'left'}`}>
            <div dangerouslySetInnerHTML={{ __html: section.content }} />
          </div>
        );

      case 'form':
        return (
          <div className="py-12 px-8 bg-slate-50 rounded-lg" id="form">
            <div className="max-w-md mx-auto">
              <h2 className="text-2xl font-bold text-center mb-2">{section.headline}</h2>
              <p className="text-muted-foreground text-center mb-6">{section.subheadline}</p>
              <div className="space-y-4">
                {(section.fields || []).map(field => (
                  <div key={field.id}>
                    <label className="block text-sm font-medium mb-1">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea 
                        className="w-full p-2 border rounded-lg" 
                        placeholder={field.placeholder}
                        rows={3}
                      />
                    ) : (
                      <input 
                        type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
                        className="w-full p-2 border rounded-lg" 
                        placeholder={field.placeholder}
                      />
                    )}
                  </div>
                ))}
                <button 
                  className="w-full py-3 text-white font-semibold rounded-lg"
                  style={{ backgroundColor: primaryColor }}
                >
                  {pageData.form_settings.submit_button_text || 'Submit'}
                </button>
              </div>
            </div>
          </div>
        );

      case 'cta':
        return (
          <div 
            className="py-16 px-8 text-center rounded-lg"
            style={{ backgroundColor: section.background_color || primaryColor }}
          >
            <h2 className="text-3xl font-bold text-white mb-3">{section.headline}</h2>
            <p className="text-lg text-white/90 mb-6">{section.subheadline}</p>
            <button className="px-8 py-3 bg-white text-slate-900 font-semibold rounded-lg">
              {section.cta_text}
            </button>
          </div>
        );

      default:
        return (
          <div className="py-8 px-4 text-center text-muted-foreground border-2 border-dashed rounded-lg">
            {section.type} preview
          </div>
        );
    }
  }
};

export default LandingPageBuilderPage;
